import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { AttendanceEventTimeline } from '../../components/AttendanceEventTimeline';
import { AttendanceEventSourceBadge } from '../../components/AttendanceEventSourceBadge';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getAttendanceCorrectionModuleCapabilities } from '../../config/attendanceCorrectionCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getAttendanceDetails } from '../../services/attendanceService';
import type { AttendanceDetails } from '../../types/attendance';
import { attendanceErrorMessage } from '../../utils/attendanceErrors';
import {
  formatAttendanceDate,
  formatAttendanceTimestamp,
  formatDurationMinutes,
  formatTime,
} from '../../utils/dateTime';
import { breakSegmentsFromEvents } from '../../utils/checkoutBreak';
import { draftFromAttendanceDetails } from '../../utils/attendanceCorrectionDraft';
import { formatLeaveDays, ordinal } from '../../utils/lateArrivalRules';
import { isAllowedAttendanceCorrectionDate } from '../../utils/attendanceCorrectionFilters';
import { resolveAttendanceLocationMode } from '../../utils/attendanceLocationMode';
import { isTrackedAttendanceSource } from '../../utils/locationTracking';
import { loadTrackingSettings } from '../../hooks/useLocationTracking';
import type { LocationTrackingSettings } from '../../types/locationTracking';
import { formatStatusWithNotes } from '../../utils/attendanceDayStatus';
import {
  resolveCheckedOutWorkHoursCoverage,
  workHoursCoverageLabel,
} from '../../utils/workHoursCoverage';
import {
  checkedOutTotalWorkMinutes,
  formatPolicyHours,
  formatTotalWorkHours,
  isAttendanceCheckedOut,
  resolveTotalHoursCoverage,
} from '../../utils/totalHours';

type Props = NativeStackScreenProps<RootStackParamList, 'AttendanceDetails'>;

function Field({
  label,
  value,
  palette,
  valueColor,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
  valueColor?: string;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.label, { color: palette.textMuted }]}>{label}</Text>
      <Text
        style={[
          styles.value,
          { color: valueColor ?? palette.textPrimary },
        ]}
      >
        {value}
      </Text>
    </View>
  );
}

function isValidDateParam(value: string | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

export function AttendanceDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const correctionCapabilities = getAttendanceCorrectionModuleCapabilities(
    auth.permissions,
  );
  const attendanceDate = route.params.attendanceDate;
  const employeeId =
    typeof route.params.employeeId === 'number' &&
    Number.isInteger(route.params.employeeId) &&
    route.params.employeeId > 0
      ? route.params.employeeId
      : undefined;
  const [details, setDetails] = useState<AttendanceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noData, setNoData] = useState(false);
  const [trackingSettings, setTrackingSettings] =
    useState<LocationTrackingSettings | null>(null);

  const themed = useMemo(
    () => ({
      section: { ...typography.sectionTitle, color: palette.textPrimary },
      verified: { ...typography.caption, color: palette.success },
    }),
    [palette],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      if (!isValidDateParam(attendanceDate)) {
        setError('Attendance record was not found.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const next = await getAttendanceDetails(
          attendanceDate,
          employeeId != null ? { employeeId } : undefined,
        );
        setDetails(next);
        setNoData(next == null);
      } catch (caught) {
        setError(
          attendanceErrorMessage(caught, 'Unable to load attendance details.'),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [attendanceDate, employeeId],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const trackingSource = details?.checkIn.source ?? details?.attendanceMode ?? null;
  useEffect(() => {
    if (!details?.attendanceId || !trackingSource) return;
    let cancelled = false;
    loadTrackingSettings().then(next => {
      if (!cancelled) setTrackingSettings(next);
    });
    return () => {
      cancelled = true;
    };
  }, [details?.attendanceId, trackingSource]);
  const showTracking = Boolean(
    details?.attendanceId &&
      (details.checkIn.timestamp || details.checkIn.localTimestamp) &&
      isTrackedAttendanceSource(trackingSource, trackingSettings),
  );

  // Hours status / Total Work Hours / Not Covered only after a valid check-out.
  const isCheckedOut = details
    ? isAttendanceCheckedOut({
        isCheckedOut: details.isCheckedOut,
        checkOutTime: details.checkOut.localTimestamp ?? details.checkOut.timestamp,
      })
    : false;
  const hoursCoverage = resolveCheckedOutWorkHoursCoverage(
    isCheckedOut,
    details?.durations.workedMinutes,
    details?.durations.expectedWorkMinutes,
  );
  const hoursCoverageText = workHoursCoverageLabel(hoursCoverage);
  const totalWorkMinutes = checkedOutTotalWorkMinutes({
    isCheckedOut,
    totalWorkMinutes: details?.durations.totalHoursMinutes,
    workedMinutes: details?.durations.workedMinutes,
    breakMinutes: details?.durations.breakMinutes,
  });
  const totalHoursCoverage = resolveTotalHoursCoverage(
    totalWorkMinutes,
    details?.durations.spreadMinutes,
  );
  const locationMode = details
    ? resolveAttendanceLocationMode({
        attendanceMode: details.attendanceMode,
        checkIn: details.checkIn,
        checkOut: details.checkOut,
      })
    : null;
  const breakSegments = details
    ? breakSegmentsFromEvents(details.events)
    : [];
  const correctionDraft = draftFromAttendanceDetails(details);
  const canRequestCorrection =
    correctionCapabilities.canApply &&
    employeeId == null &&
    isAllowedAttendanceCorrectionDate(attendanceDate) &&
    !correctionDraft.blocked;

  return (
    <PermissionGuard
      permission={PERMISSIONS.ATTENDANCE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader title="Attendance Details" onBack={navigation.goBack} />
          <AppLoader message="Loading attendance details" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader title="Attendance Details" onBack={navigation.goBack} />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : noData && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader title="Attendance Details" onBack={navigation.goBack} />
          <EmptyState
            title="No data available"
            message={`No attendance was recorded on ${formatAttendanceDate(attendanceDate)}.`}
            actionTitle="Refresh"
            onAction={() => load()}
          />
        </AppScreen>
      ) : details ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader title="Attendance Details" onBack={navigation.goBack} />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <Text style={themed.section}>Summary</Text>
              <Field
                label="Date"
                value={formatAttendanceDate(details.attendanceDate)}
                palette={palette}
              />
              <Field
                label="Status"
                value={formatStatusWithNotes(
                  details.statusLabel,
                  details.statusNotes,
                )}
                palette={palette}
              />
              {details.statusReason ? (
                <Field
                  label="Reason"
                  value={details.statusReason}
                  palette={palette}
                />
              ) : null}
              {locationMode ? (
                <Field
                  label="Attendance type"
                  value={locationMode}
                  palette={palette}
                />
              ) : null}
              {details.lateArrivalDeduction ? (
                <>
                  <Field
                    label="Late arrival"
                    value={`${ordinal(details.lateArrivalDeduction.occurrenceNumber)} this month (${formatDurationMinutes(details.lateArrivalDeduction.lateMinutes)} late)`}
                    palette={palette}
                  />
                  <Field
                    label="Leave deducted"
                    value={
                      details.lateArrivalDeduction.leaveDeductedDays > 0
                        ? `${formatLeaveDays(details.lateArrivalDeduction.leaveDeductedDays)} ${details.lateArrivalDeduction.leaveTypeName ?? ''}`.trim()
                        : 'None'
                    }
                    palette={palette}
                  />
                  {details.lateArrivalDeduction.lopDays > 0 ? (
                    <Field
                      label="LOP"
                      value={formatLeaveDays(details.lateArrivalDeduction.lopDays)}
                      palette={palette}
                      valueColor={palette.error}
                    />
                  ) : null}
                </>
              ) : null}
              <Field
                label="Shift"
                value={details.shift.shiftName ?? 'Not available'}
                palette={palette}
              />
              <Field
                label="Scheduled start"
                value={formatAttendanceTimestamp(
                  details.shift.scheduledStartTime,
                  details.shift.scheduledStartTime,
                )}
                palette={palette}
              />
              <Field
                label="Scheduled end"
                value={formatAttendanceTimestamp(
                  details.shift.scheduledEndTime,
                  details.shift.scheduledEndTime,
                )}
                palette={palette}
              />
              {details.faceAttendanceVerified === true ? (
                <Text style={themed.verified}>
                  Verified using face attendance
                </Text>
              ) : null}
            </AppCard>

            <AppCard style={styles.card}>
              <Text style={themed.section}>Check-in</Text>
              <Field
                label="Time"
                value={formatAttendanceTimestamp(
                  details.checkIn.localTimestamp,
                  details.checkIn.timestamp,
                )}
                palette={palette}
              />
              <Field
                label="Location"
                value={details.checkIn.locationName ?? 'Not available'}
                palette={palette}
              />
              <Field
                label="Device"
                value={details.checkIn.deviceName ?? 'Not available'}
                palette={palette}
              />
              <View style={styles.field}>
                <Text style={[styles.label, { color: palette.textMuted }]}>
                  Source
                </Text>
                {details.checkIn.source || details.checkIn.isManuallyEdited ? (
                  <AttendanceEventSourceBadge
                    punch={details.checkIn}
                    editedTime={
                      details.checkIn.localTimestamp ?? details.checkIn.timestamp
                    }
                  />
                ) : (
                  <Text style={[styles.value, { color: palette.textPrimary }]}>
                    Not available
                  </Text>
                )}
              </View>
              {showTracking ? (
                <AppButton
                  title="Tracking Details"
                  variant="secondary"
                  onPress={() =>
                    navigation.navigate(routes.locationTrackingDetails, {
                      attendanceId: details.attendanceId!,
                    })
                  }
                />
              ) : null}
            </AppCard>

            <AppCard style={styles.card}>
              <Text style={themed.section}>Check-out</Text>
              <Field
                label="Time"
                value={formatAttendanceTimestamp(
                  details.checkOut.localTimestamp,
                  details.checkOut.timestamp,
                )}
                palette={palette}
              />
              <Field
                label="Location"
                value={details.checkOut.locationName ?? 'Not available'}
                palette={palette}
              />
              <Field
                label="Device"
                value={details.checkOut.deviceName ?? 'Not available'}
                palette={palette}
              />
              <View style={styles.field}>
                <Text style={[styles.label, { color: palette.textMuted }]}>
                  Source
                </Text>
                {details.checkOut.source || details.checkOut.isManuallyEdited ? (
                  <AttendanceEventSourceBadge
                    punch={details.checkOut}
                    editedTime={
                      details.checkOut.localTimestamp ??
                      details.checkOut.timestamp
                    }
                  />
                ) : (
                  <Text style={[styles.value, { color: palette.textPrimary }]}>
                    Not available
                  </Text>
                )}
              </View>
            </AppCard>

            <AppCard style={styles.card}>
              <Text style={themed.section}>Duration</Text>
              <Field
                label="Worked"
                value={formatDurationMinutes(details.durations.workedMinutes)}
                palette={palette}
                valueColor={
                  hoursCoverage === 'covered'
                    ? palette.success
                    : hoursCoverage === 'not_covered'
                    ? palette.error
                    : undefined
                }
              />
              {hoursCoverageText ? (
                <Field
                  label="Hours status"
                  value={hoursCoverageText}
                  palette={palette}
                  valueColor={
                    hoursCoverage === 'covered'
                      ? palette.success
                      : palette.error
                  }
                />
              ) : null}
              <Field
                label="Total Break"
                value={formatDurationMinutes(details.durations.breakMinutes)}
                palette={palette}
              />
              {breakSegments.map((segment, index) => {
                const startLabel = formatTime(segment.startAt) ?? '—';
                const endLabel = segment.isOpen
                  ? 'In progress'
                  : formatTime(segment.endAt) ?? '—';
                const durationLabel = segment.isOpen
                  ? 'In progress'
                  : formatDurationMinutes(segment.durationMinutes);
                return (
                  <Field
                    key={`break-${index}-${segment.startAt ?? index}`}
                    label={
                      breakSegments.length > 1
                        ? `Break ${index + 1}`
                        : 'Break'
                    }
                    value={`${startLabel} – ${endLabel} (${durationLabel})`}
                    palette={palette}
                  />
                );
              })}
              <Field
                label="Expected Work Hours"
                value={formatPolicyHours(
                  details.durations.expectedWorkMinutes,
                  'Not available',
                )}
                palette={palette}
              />
              <Field
                label="Total Work Hours"
                value={formatTotalWorkHours(totalWorkMinutes, 'Not available')}
                palette={palette}
                valueColor={
                  totalHoursCoverage === 'not_covered'
                    ? palette.error
                    : undefined
                }
              />
              <Field
                label="Late"
                value={formatDurationMinutes(details.durations.lateMinutes)}
                palette={palette}
              />
              <Field
                label="Early departure"
                value={formatDurationMinutes(
                  details.durations.earlyDepartureMinutes,
                )}
                palette={palette}
              />
              <Field
                label="Overtime"
                value={formatDurationMinutes(details.durations.overtimeMinutes)}
                palette={palette}
              />
            </AppCard>

            <AppCard style={styles.card}>
              <Text style={themed.section}>Event timeline</Text>
              <AttendanceEventTimeline events={details.events} />
            </AppCard>

            {canRequestCorrection ? (
              <AppButton
                title="Request attendance correction"
                onPress={() =>
                  navigation.navigate(routes.attendanceCorrectionHome, {
                    initialAttendanceDate: attendanceDate,
                  })
                }
              />
            ) : null}
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: spacing.sm, marginBottom: spacing.md },
  field: { gap: 2 },
  label: { ...typography.caption },
  value: { ...typography.body },
});
